import ApiService from './ApiService'
import NotificationService from './NotificationService'
import FirebaseAuthService from './FirebaseAuthService'
import LiveSupportService from './LiveSupportService'

export async function apiSignIn(data) {
    return ApiService.fetchData({
        url: '/auth/login',
        method: 'post',
        data,
    })
}

export async function apiSignUp(data) {
    return ApiService.fetchData({
        url: '/sign-up',
        method: 'post',
        data,
    })
}

export async function apiSignOut(data) {
    try {
        console.log('🔍 [DASHBOARD AUTH SERVICE] Starting logout process...');

        // Set Firebase support user as offline
        if (data?.user) {
            console.log('🔍 [DASHBOARD AUTH SERVICE] Setting Firebase support user as offline...');
            await setFirebaseUserOffline(data.user);
        } else {
            // If no user data provided, still set support user offline
            console.log('🔍 [DASHBOARD AUTH SERVICE] No user data provided, setting support user offline...');
            await setFirebaseUserOffline({ _id: 'support' });
        }

        // Clean up browser behavior detection
        console.log('🔍 [DASHBOARD AUTH SERVICE] Cleaning up browser behavior detection...');
        if (window.dashboardAuthCleanup) {
            window.dashboardAuthCleanup();
            window.dashboardAuthCleanup = null;
        }

        // Unregister device token for push notifications
        console.log('🔍 [DASHBOARD AUTH SERVICE] Attempting device token unregistration...');
        await NotificationService.unregisterCurrentDeviceToken();

        console.log('🔍 [DASHBOARD AUTH SERVICE] Making logout API call...');
        return ApiService.fetchData({
            url: '/auth/logout',
            method: 'post',
            data,
        })
    } catch (error) {
        console.log('❌ [DASHBOARD AUTH SERVICE] Logout error:', error);
        // Continue with logout even if cleanup fails
        console.log('🔍 [DASHBOARD AUTH SERVICE] Continuing with logout despite error...');
        return ApiService.fetchData({
            url: '/auth/logout',
            method: 'post',
            data,
        })
    }
}

/**
 * Set Firebase user as online in users collection
 * @param {Object} userData - User data from login response
 */
export async function setFirebaseUserOnline(userData) {
    try {
        // Setting Firebase user as online

        // Use FirebaseAuthService to update user online status
        const success = await FirebaseAuthService.updateUserOnlineStatus(
            userData._id,
            true,
            userData.userType || 'admin'
        );

        if (success) {
            // Firebase user online status updated
        } else {
            console.log('⚠️ [DASHBOARD AUTH SERVICE] Failed to update Firebase user online status');
        }

        return success;
    } catch (error) {
        console.log('❌ [DASHBOARD AUTH SERVICE] Error setting Firebase user online:', error);
        return false;
    }
}

/**
 * Set Firebase user as offline in users collection
 * @param {Object} userData - User data
 */
export async function setFirebaseUserOffline(userData) {
    try {
        // Setting Firebase user as offline

        // Use FirebaseAuthService to update user online status
        const success = await FirebaseAuthService.updateUserOnlineStatus(
            userData._id,
            false,
            userData.userType || 'admin'
        );

        if (success) {
            // Firebase user offline status updated
        } else {
            console.log('⚠️ [DASHBOARD AUTH SERVICE] Failed to update Firebase user offline status');
        }

        return success;
    } catch (error) {
        console.log('❌ [DASHBOARD AUTH SERVICE] Error setting Firebase user offline:', error);
        return false;
    }
}

/**
 * Initialize dashboard user presence after successful login
 * @param {Object} userData - User data from login response
 */
export async function initializeDashboardPresence(userData) {
    try {
        console.log('🔍 [DASHBOARD AUTH SERVICE] Initializing presence for user:', userData._id);

        const userType = userData.userType === 'support' ? 'support' : 'admin';

        // Set Firebase support user as online
        await setFirebaseUserOnline(userData);

        // Set up browser behavior detection for status management
        setupBrowserBehaviorDetection(userData);

        console.log('✅ [DASHBOARD AUTH SERVICE] Firebase user online status updated and browser detection setup');

        return true;
    } catch (error) {
        console.log('❌ [DASHBOARD AUTH SERVICE] Error initializing presence:', error);
        return false;
    }
}

/**
 * Set up browser behavior detection for user status management
 * @param {Object} userData - User data
 */
function setupBrowserBehaviorDetection(userData) {
    try {
        console.log('🔍 [DASHBOARD AUTH SERVICE] Setting up browser behavior detection');

        // Handle page visibility changes
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                console.log('🔍 [DASHBOARD AUTH SERVICE] Page became visible, setting user online');
                await setFirebaseUserOnline(userData);
            } else {
                console.log('🔍 [DASHBOARD AUTH SERVICE] Page became hidden, setting user offline');
                await setFirebaseUserOffline(userData);
            }
        };

        // Handle window focus/blur
        const handleFocus = async () => {
            console.log('🔍 [DASHBOARD AUTH SERVICE] Window focused, setting user online');
            await setFirebaseUserOnline(userData);
        };

        const handleBlur = async () => {
            console.log('🔍 [DASHBOARD AUTH SERVICE] Window blurred, setting user offline');
            await setFirebaseUserOffline(userData);
        };

        // Handle page unload
        const handleBeforeUnload = async () => {
            console.log('🔍 [DASHBOARD AUTH SERVICE] Page unloading, setting user offline');
            await setFirebaseUserOffline(userData);
        };

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Store cleanup function
        window.dashboardAuthCleanup = () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };

        console.log('✅ [DASHBOARD AUTH SERVICE] Browser behavior detection setup complete');
    } catch (error) {
        console.log('❌ [DASHBOARD AUTH SERVICE] Error setting up browser behavior detection:', error);
    }
}

export async function apiForgotPassword(data) {
    return ApiService.fetchData({
        url: '/forgot-password',
        method: 'post',
        data,
    })
}

export async function apiResetPassword(data) {
    return ApiService.fetchData({
        url: '/reset-password',
        method: 'post',
        data,
    })
}
